import { createSlice, PayloadAction, Slice } from "@reduxjs/toolkit";
import { AccessibleFeature, fetchAccountAccess } from "Services/CommonServices";
import { AppThunk, RootState } from "utils/store";
import { logOutAction } from "redux/authSlice";

export interface FeaturesState {
  byKey: Record<string, AccessibleFeature>;
  loaded: boolean;
  loading: boolean;
  lastFetched: number;
  error: string;
}

const initialState: FeaturesState = {
  byKey: {},
  loaded: false,
  loading: false,
  lastFetched: 0,
  error: "",
};

export const featuresSlice: Slice<FeaturesState> = createSlice({
  name: "features",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(logOutAction, () => initialState);
  },
  reducers: {
    setFeaturesLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) state.error = "";
    },
    setFeatures: (state, action: PayloadAction<AccessibleFeature[]>) => {
      state.byKey = Array.isArray(action.payload)
        ? action.payload.reduce<Record<string, AccessibleFeature>>((acc, f) => {
            acc[f.key] = f;
            return acc;
          }, {})
        : {};
      state.loaded = true;
      state.loading = false;
      state.lastFetched = Date.now();
      state.error = "";
    },
    setFeaturesError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearFeatures: (state) => {
      state.byKey = {};
      state.loaded = false;
      state.loading = false;
      state.lastFetched = 0;
      state.error = "";
    },
  },
});

export const {
  setFeaturesLoading,
  setFeatures,
  setFeaturesError,
  clearFeatures,
} = featuresSlice.actions;

// Backend access cache TTL is 24h — match it on the frontend so a long
// session re-fetches once per day.
const REFETCH_AFTER_MS = 24 * 60 * 60 * 1000;

export const loadFeatures =
  (force: boolean = false): AppThunk =>
  async (dispatch, getState) => {
    const { loading, lastFetched, loaded } = getState().features;
    if (loading) return;
    if (!force && loaded && Date.now() - lastFetched < REFETCH_AFTER_MS) {
      return;
    }
    dispatch(setFeaturesLoading(true));
    try {
      const features = await fetchAccountAccess();
      dispatch(setFeatures(features));
    } catch (err: any) {
      const message =
        err?.message ?? err?.errorMessage ?? "Failed to load features";
      dispatch(setFeaturesError(message));
    }
  };

export const refreshFeatures = (): AppThunk => loadFeatures(true);

export const selectFeatureMap = (state: RootState) => state.features.byKey;
export const selectFeatureByKey =
  (key: string) => (state: RootState) =>
    state.features.byKey[key];
export const selectFeaturesLoaded = (state: RootState) => state.features.loaded;
export const selectFeaturesLoading = (state: RootState) =>
  state.features.loading;
export const selectFeaturesLastFetched = (state: RootState) =>
  state.features.lastFetched;
export const selectFeaturesError = (state: RootState) => state.features.error;

export default featuresSlice.reducer;
